import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, withCtx as _withCtx, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, normalizeStyle as _normalizeStyle, createBlock as _createBlock } from "vue"

const _hoisted_1 = { key: 0 }
const _hoisted_2 = { class: "photo-template" }
const _hoisted_3 = ["src", "title"]
const _hoisted_4 = { class: "header-cell" }

import { computed, reactive, ref, onBeforeMount, watch } from "vue";
import TileCard from "@/components/util/TileCard.vue";
import CalendarDay from "@/components/Calendar/CalendarDay.vue";
import { useCalendarStore } from "@/stores/calendar";
import DxDataGrid, { DxPaging, DxPager, DxMasterDetail, DxDataGridTypes, DxScrolling, DxStateStoring, DxFilterRow, DxHeaderFilter } from "devextreme-vue/data-grid";
import { CalendarLine } from "@/services/entities/Reporting/CalendarLine";
import { CalendarDayModel } from "@/services/entities/Reporting/CalendarDayModel";
import ReportTable from "../tables/ReportTable.vue";
import { useReportingMonthStore } from "@/stores/reporting";
import { UserRole } from "@/services/entities/UserItem";
import { useUserStore } from "@/stores/user";
import { useAzureStore } from "@/stores/azure";
import { useServiceTeamsStore } from "@/stores/serviceTeams";
import { Authorization } from "@/services/authenticate";
import { configureDataGridColumnsOrder } from "@/common/util";



export default /*@__PURE__*/_defineComponent({
  __name: 'CalendarTable',
  props: {
    date: {}
  },
  setup(__props: any) {

const calendarStore = useCalendarStore();
const reportingStore = useReportingMonthStore();
const userStore = useUserStore();
const azureStore = useAzureStore();
const teamsStore = useServiceTeamsStore();


const props = __props;

onBeforeMount(async () => {
  await teamsStore.loadServiceTeams();
});


watch(() => calendarStore.getAllLoaded, async (v) => {
  if (!v) {
    if (gridData.value.length > 0) {
      console.log('TLIds', gridData.value.map((a) => a.teamLeaderId));
      await azureStore.loadUserPhotos(gridData.value.map((a) => a.teamLeaderId));
      gridData.value.forEach((a: any) => {
        a.photo = azureStore.getUsersPhotos.find((p: any) => p.id === a.teamLeaderId)?.blob;
      });
    }    
  }
})


const calendarGrid = ref()
let isConfigured = false;

const state = reactive({
  loadDetail: false,
  id: "" as string,
});

const columns = computed(() => {
  const cols = [
    {
      dataField: "teamMember",
      caption: "Team Member",
      fixed: "sticky",
      cssClass: 'name-cell',
      columnAutoWidth: true,
      allowFiltering: true,
    },
    {
      dataField: "teamLeader.fullName",
      caption: "TL",
      cellTemplate: "tl-cell-template",      
      width: "70px",
      allowFiltering: true,
    },
    {
      dataField: "teamName",
      caption: "Team",
      width: "150px",
      allowFiltering: true,
    }
  ];
  // Add a column for each day, using the current daysInMonth
  Array.from({ length: calendarStore.getAll.daysInMonth.length }, (_, i) => i + 1).forEach((day: number) => {
    cols.push({
      alignment: "center",
      allowEditing: false,
      allowExporting: false,
      allowFiltering: false,
      allowGrouping: false,
      allowHeaderFiltering: false,
      allowHiding: false,
      allowReordering: false,
      allowResizing: false,
      allowSearch: false,
      allowSorting: false,      
      dataField: 'day_' + day,
      caption: `${day}.`,
      cellTemplate: "cellTemplate",
      headerCellTemplate: "headerCellTemplate",      
      cssClass: getCellBackgroundClass(day),
    });
  });
  return cols;
});

const gridData = computed(() => {
  return (calendarStore.getAll.lines || []).map((line: CalendarLine) => {
    const row: Record<string, any> = {
      teamMember: line.teamMember.fullName,
      teamLeaderId: line.teamMember.teamLeaderId,
      teamLeader: line.teamMember.teamLeader,
      id: line.teamMember.id,
    };
    row["teamName"] = getUserServiceTeam(row);
    // Populate each day's data
    Array.from({ length: calendarStore.getAll.daysInMonth.length }, (_, i) => i + 1).forEach((day: number) => {
      row['day_' + day] = getDayData(line, day);
    });
    return row;
  });
});

function  onContentReady(e: any) {
  // ensure default column order
  const defaultOrder = ["teamMember", "teamLeader.fullName", "teamName"];    
  if (!isConfigured) {
    configureDataGridColumnsOrder(e, defaultOrder);
    isConfigured = true;
  }   
};


function getUserServiceTeam(data: any) {
  let teams = teamsStore.getServiceTeams.filter(t => t.members?.find(m => m.id === data.id) || t.teamLead?.find(tl => tl.id === data.id));    
  let teamNames = teams.map(t => t.teamName).sort();
  if (teamNames.length > 0)
    return teamNames.join(', ');
  else
    return "";
}

function canShowDetail(): boolean {
  return userStore.getUserRole === UserRole.Administrator || 
    userStore.getUserRole === UserRole.TeamLeader || 
    userStore.getUserRole === UserRole.TeamLeaderReport; 
}

function getDayData(line: CalendarLine, dayNum: number): CalendarDayModel {  
  // Return the CalendarDayModel for this day (or a default)
  return line.days[dayNum - 1] || {
    hours: 0,
    isAbsent: false,
    outOfOffice: false,
    isWorkDay: false,
    date: null,
  };
}

function getCellBackgroundClass(day: number): string {
  // Determine the background class based on the column data
  if (!calendarStore.getAll.daysInMonth[day - 1]) {
    return "is-work-dayOff base-cell"; // Default class for non-work days
  }
  return 'base-cell';
}

function getGridWidth(): string {
  var width = calendarStore.getAll.daysInMonth.length * 3 + 30; 
  return `width: min(100%, ${width}em);`;
}

async function loadReportTable(e: DxDataGridTypes.SelectionChangedEvent) {
  e.component.collapseAll(-1);
  state.id = e.key; // Store the selected user ID
  // state.detailUser = user;
  await loadReportTableData();
  calendarGrid.value?.instance?.updateDimensions();
}

async function loadReportTableData() {
  reportingStore.clearReportingMonth();
  state.loadDetail = true;
  await reportingStore.loadReportingMonth(
    props.date.year,
    props.date.month + 1,
    state.id,
  );
  calendarGrid.value?.instance?.updateDimensions();
}

const createBlobUrl = (data: any) => {
  try {
    if (data.teamLeaderId) {
      const photo = azureStore.getUsersPhotos.find((p: any) => p.id === data.teamLeaderId)?.blob;
      if (photo) {
        return URL.createObjectURL(photo);
      }
    }
  } catch (e) {
    console.error("Error creating blob URL", e);
    return null;
  }
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(TileCard, {
    id: "calendar-table",
    class: "calendar-table-card",
    loaded: !_unref(calendarStore).getAllLoaded
  }, {
    body: _withCtx(() => [
      _createVNode(_unref(DxDataGrid), {
        "data-source": gridData.value,
        "show-borders": true,
        "hover-state-enabled": true,
        "column-auto-width": false,
        "row-alternation-enabled": true,
        "word-wrap-enabled": false,
        columns: columns.value,
        "key-expr": "id",
        id: "calendarGrid",
        style: _normalizeStyle(getGridWidth()),
        onRowExpanding: loadReportTable,
        "cell-hint-enabled": true,
        onContentReady: onContentReady,
        ref_key: "calendarGrid",
        ref: calendarGrid
      }, {
        masterDetailTemplate: _withCtx(({ data }) => [
          (state.loadDetail)
            ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
                _createVNode(TileCard, {
                  id: "report-table",
                  loaded: !_unref(reportingStore).getAllLoaded
                }, {
                  body: _withCtx(() => [
                    _createVNode(ReportTable, {
                      reportingMonth: _unref(reportingStore).getAll,
                      state: state,
                      "onUpdate:modelValue": loadReportTableData,
                      readonly: true
                    }, {
                      buttons: _withCtx(() => [...(_cache[0] || (_cache[0] = []))]),
                      _: 1
                    }, 8, ["reportingMonth", "state"])
                  ]),
                  _: 1
                }, 8, ["loaded"])
              ]))
            : _createCommentVNode("", true)
        ]),
        "tl-cell-template": _withCtx(({ data }) => [
          _createElementVNode("div", _hoisted_2, [
            _createElementVNode("img", {
              src: createBlobUrl(data.data),
              alt: "Direct manager",
              class: "person-photo person-photo--rounded",
              title: data.data?.teamLeader?.fullName
            }, null, 8, _hoisted_3)
          ])
        ]),
        cellTemplate: _withCtx(({ data }) => [
          _createVNode(CalendarDay, {
            dayData: data.value
          }, null, 8, ["dayData"])
        ]),
        headerCellTemplate: _withCtx(({ data }) => [
          _createElementVNode("div", _hoisted_4, _toDisplayString(data.column.caption), 1)
        ]),
        default: _withCtx(() => [
          _createVNode(_unref(DxMasterDetail), {
            enabled: canShowDetail(),
            template: "masterDetailTemplate"
          }, null, 8, ["enabled"]),
          _createVNode(_unref(DxStateStoring), {
            enabled: true,
            type: "localStorage",
            "storage-key": "calendarGrid"
          }),
          _createVNode(_unref(DxScrolling), { mode: "standard" }),
          _createVNode(_unref(DxFilterRow), { visible: true }),
          _createVNode(_unref(DxHeaderFilter), { visible: true }),
          _createVNode(_unref(DxPaging), { "page-size": 10 }),
          _createVNode(_unref(DxPager), {
            "show-page-size-selector": true,
            "allowed-page-sizes": [10, 20, 30]
          })
        ]),
        _: 1
      }, 8, ["data-source", "columns", "style"])
    ]),
    _: 1
  }, 8, ["loaded"]))
}
}

})